import { useMemo } from 'react'
import { applicationsStore } from 'pages/ApplicationsPage/model/applicationsStore'
import { getCountryNameByIso2 } from 'shared/utils/countryNameByIso2'

// Hook for UniversityExplorationSubmission component
// Note: applicationsStore should be loaded at page level (ClassroomPage) to avoid race conditions
export const useUniversityExplorationSubmission = ({ assignment, student }) => {
	const studentId = student?.id

	// Get applications for this specific student
	const studentApplications = useMemo(() => {
		if (!studentId) return []
		return applicationsStore.getApplicationsByStudentId(studentId)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [studentId, applicationsStore.applications])

	const isLoading = false // Applications are loaded from store, no separate loading state
	const error = null

	// Group universities by country from applications
	const universitiesByCountry = useMemo(() => {
		if (!studentApplications || studentApplications.length === 0) {
			return []
		}

		// Extract universities from applications (each application = one university)
		const universities = studentApplications.map(app => {
			const universityName =
				app?.university ||
				app?.universityName ||
				app?.college ||
				'Unknown University'

			// Country comes as ISO2 code from API (e.g., 'AU', 'US', 'CA')
			const countryCode = app?.country || null

			// Convert ISO2 code to country name
			let countryName = 'Unknown Country'
			if (countryCode) {
				countryName = getCountryNameByIso2(countryCode) || countryCode
			}

			return {
				name: universityName,
				countryCode,
				countryName,
				original: app,
			}
		})

		// Group by country name
		const grouped = new Map()
		universities.forEach(uni => {
			const countryKey = uni.countryName || 'Unknown Country'
			if (!grouped.has(countryKey)) {
				grouped.set(countryKey, {
					countryName: countryKey,
					universities: [],
				})
			}
			grouped.get(countryKey).universities.push(uni)
		})

		// Convert to array and sort by country name
		return Array.from(grouped.values()).sort((a, b) =>
			a.countryName.localeCompare(b.countryName)
		)
	}, [studentApplications])

	// Get response text from student data
	const responseText =
		student?.response_text ||
		student?.responseText ||
		student?.assignmentData?.response_text ||
		assignment?.students?.find(s => s.id === student?.id)?.response_text ||
		''

	return {
		universitiesByCountry,
		isLoading,
		error,
		responseText,
		hasResponse: Boolean(responseText && responseText.trim()),
	}
}

export default useUniversityExplorationSubmission
